<template>
  <fragment v-if="campaign">
    <div v-if="campaignEditAssetsData && campaignEditAssetsData.IsAddAssetEnabled">
      <h5 class="section-title">{{ $t("CampaignEdit.Assets.AddAssets") }}</h5>
      <div class="dropdown-w">
        <label>{{ $t('CampaignEdit.ProjectTypeId') }}</label>
        <b-tooltip position="is-bottom" type="is-dark" class="assets-tooltip">
          <span>
            <icon-base width="16" height="16" view-box="0 0 16 16" icon-name="chart-pie"><icon-question-circle/></icon-base>
          </span>
          <template #content>
            <p>{{ $t("CampaignEdit.ProjectTypeTooltip") }}</p>
          </template>
        </b-tooltip>
        <b-dropdown v-model="selectedProjectType" scrollable aria-role="list" :expanded="true"
                    @active-change="onDropdownActiveChange" @change="onSelectedProjectTypeChange($event)">
          <template #trigger>
            <b-button>
              <span class="dropdown-trigger-value">{{
                  selectedProjectTypeListItem ? selectedProjectTypeListItem.Text : $t("CampaignEdit.Assets.CampaignTypePlaceholder")
                }}</span>
              <icon-base width="12" height="7" view-box="0 0 12 7" icon-name="caret-down">
                <icon-caret-down/>
              </icon-base>
            </b-button>
          </template>
          <b-dropdown-item v-for="projectType in campaignEditAssetsData.ProjectTypes" :key="+projectType.Value"
                           :value="+projectType.Value"
                           aria-role="listitem" class="dropdown-item-with-icon">
            <span class="text">{{ projectType.Text }}</span>
          </b-dropdown-item>
        </b-dropdown>
      </div>
      <div class="dropdown-w" v-if="showAssetsSelectList">
        <label>{{ $t('CampaignEdit.Asset') }}</label>
        <b-dropdown v-model="selectedAssetId" scrollable aria-role="list" :expanded="true"
                    @active-change="onDropdownActiveChange" @change="onSelectedAssetChange($event)">
          <template #trigger>
            <b-button>
              <span class="dropdown-trigger-value">{{
                  selectedClientAsset ? selectedClientAsset.title : $t("CampaignEdit.AssetPlaceholder")
                }}</span>
              <icon-base width="12" height="7" view-box="0 0 12 7" icon-name="caret-down">
                <icon-caret-down/>
              </icon-base>
            </b-button>
          </template>
          <b-dropdown-item custom aria-role="listitem" class="filter-search">
            <b-input v-model="clientAssetsSearch" class="search" :placeholder="$t('Search')" expanded/>
          </b-dropdown-item>
          <b-dropdown-item v-for="clientAsset in clientAssetsFiltered" :key="clientAsset.id" :value="clientAsset.id"
                           aria-role="listitem" class="dropdown-item-with-icon">
            <span class="text">{{ clientAsset.title }}</span>
          </b-dropdown-item>
        </b-dropdown>
      </div>
      <div class="dropdown-w" v-if="showSitePagesList">
        <label>{{ $t('CampaignEdit.SitePage') }}</label>
        <b-dropdown v-model="selectedSitePageId" scrollable aria-role="list" :expanded="true"
                    @active-change="onDropdownActiveChange" @change="onSelectedSitePageChange($event)">
          <template #trigger>
            <b-button>
              <span class="dropdown-trigger-value">{{
                  selectedSitePage ? selectedSitePage.title : $t("CampaignEdit.SitePagePlaceholder")
                }}</span>
              <icon-base width="12" height="7" view-box="0 0 12 7" icon-name="caret-down">
                <icon-caret-down/>
              </icon-base>
            </b-button>
          </template>
          <b-dropdown-item custom aria-role="listitem" class="filter-search">
            <b-input v-model="sitePagesSearch" class="search" :placeholder="$t('Search')" expanded/>
          </b-dropdown-item>
          <b-dropdown-item v-for="sitePage in sitePagesFiltered" :key="sitePage.id" :value="sitePage.id"
                           aria-role="listitem" class="dropdown-item-with-icon">
            <span class="text">{{ sitePage.title }}</span>
          </b-dropdown-item>
        </b-dropdown>
      </div>
      <div class="dropdown-w" v-if="showPropertiesSelectList">
        <label>{{ $t('CampaignEdit.Properties') }}</label>
        <b-dropdown v-model="selectedPropertyId" scrollable aria-role="list" :expanded="true"
                    @active-change="onDropdownActiveChange" @change="onSelectedPropertyChange($event)">
          <template #trigger>
            <b-button>
              <span class="dropdown-trigger-value">
                {{
                  selectedClientProperty ? (selectedClientProperty.Url || selectedClientProperty.Title) : $t("CampaignEdit.PropertyPlaceholder")
                }}
              </span>
              <icon-base width="12" height="7" view-box="0 0 12 7" icon-name="caret-down">
                <icon-caret-down/>
              </icon-base>
            </b-button>
          </template>
          <b-dropdown-item custom aria-role="listitem" class="filter-search">
            <b-input v-model="clientPropertiesSearch" class="search" :placeholder="$t('Search')" expanded/>
          </b-dropdown-item>
          <b-dropdown-item v-for="clientProperty in clientPropertiesFiltered" :key="clientProperty.Id"
                           :value="clientProperty.Id"
                           aria-role="listitem" class="dropdown-item-with-icon">
            <span class="text">{{
                clientProperty.Url ? `${clientProperty.Title} - ${clientProperty.Url}` : clientProperty.Title
              }}</span>
          </b-dropdown-item>
        </b-dropdown>
      </div>
    </div>
    <div v-if="campaign.Id > 0" style="position: relative">
      <div class="connected-assets-title">{{ $t("CampaignEdit.Assets.ConnectedAssets") }}</div>
      <div class="connected-assets-desc">
        <span v-html="$t('CampaignEdit.Assets.AdditionalParameters')"></span>
        <b>FIX_TAG=testTag</b><br/>
        <span>https://example.com/path/to/page?field1=value1&field2=value2&field3=value3<b>&FIX_TAG=testTag</b></span><br/>
        <span v-html="$t('CampaignEdit.Assets.AdditionalParameters2')"></span>
      </div>
      <div class="grid-content" ref="assetsTableContainer">
        <b-table :data="campaignAssets ? campaignAssets.Data : []" hoverable sticky-header
                 :total="campaignAssets ? campaignAssets.Total : 0"
                 class="assets-table"
                 ref="assetsTable">
          <b-table-column v-for="column in columns" :key="column.id" :field="column.name" :cell-class="column.class"
                          :label="column.label"
                          :visible="column.visible && !column.forbidden"
                          :width="column.width"
                          :sortable="column.sortable"
                          :header-class="'column-th-' + column.name">
            <template v-slot="props">
              <div v-if="column.name === 'Url'" :style="getColumnStyle(column)">
                <template v-if="props.row.PreparedUrlList && props.row.PreparedUrlList.length > 0">
                  <label v-if="props.row.ProjectTypeId !== ProjectType.ExternalAPI">{{
                      $t("CampaignEdit.Assets.CampaignLinks")
                    }}</label>
                  <label v-else>{{ $t("CampaignEdit.Assets.ClickForMapping") }}</label>
                  <ul>
                    <li v-for="(url, index) in props.row.PreparedUrlList" :key="url">
                      <div v-if="index > 0">{{ $t("Or") }}</div>
                      <a :href="url" target="_blank">{{ url }}</a>
                    </li>
                  </ul>
                </template>
                <template v-if="props.row.PreparedMobileUrlList && props.row.PreparedMobileUrlList.length > 0">
                  <label>{{ $t("CampaignEdit.Assets.MobileVersion") }}</label>
                  <ul>
                    <li v-for="(url, index) in props.row.PreparedMobileUrlList" :key="url">
                      <div v-if="index > 0">{{ $t("Or") }}</div>
                      <a :href="url" target="_blank">{{ url }}</a>
                    </li>
                  </ul>
                </template>
              </div>
              <div v-else-if="column.name === 'IsDefault'" :style="getColumnStyle(column)">
                <b-field>
                  <b-checkbox v-model="props.row.IsDefault" @change.native="onIsDefaultChange(props.row)"></b-checkbox>
                </b-field>
              </div>
              <div v-else :style="getColumnStyle(column)">
                {{ props.row[column.name] }}
              </div>
            </template>
          </b-table-column>
        </b-table>
        <div class="assets-builder" v-if="campaignAssets?.Data?.find(x => x.PreparedUrlList || x.PreparedMobileUrlList)">
          <div class="title" v-t="'CampaignEdit.Assets.Builder.Title'"></div>
          <template v-if="builder.websiteURL">
            <b-field :label="$t('CampaignEdit.Assets.Builder.CampaignId.Label')">
              <b-input v-model="builder.campaignId" :placeholder="$t('CampaignEdit.Assets.Builder.CampaignId.Help')"></b-input>
            </b-field>
            <b-field :label="$t('CampaignEdit.Assets.Builder.CampaignSource.Label') + ' *'">
              <b-input v-model="builder.campaignSource" :placeholder="$t('CampaignEdit.Assets.Builder.CampaignSource.Help')"></b-input>
            </b-field>
            <b-field :label="$t('CampaignEdit.Assets.Builder.CampaignMedium.Label') + ' *'">
              <b-input v-model="builder.campaignMedium" :placeholder="$t('CampaignEdit.Assets.Builder.CampaignMedium.Help')"></b-input>
            </b-field>
            <b-field :label="$t('CampaignEdit.Assets.Builder.CampaignName.Label') + ' *'">
              <b-input v-model="builder.campaignName" :placeholder="$t('CampaignEdit.Assets.Builder.CampaignName.Help')"></b-input>
            </b-field>
            <b-field :label="$t('CampaignEdit.Assets.Builder.CampaignTerm.Label')">
              <b-input v-model="builder.campaignTerm" :placeholder="$t('CampaignEdit.Assets.Builder.CampaignTerm.Help')"></b-input>
            </b-field>
            <b-field :label="$t('CampaignEdit.Assets.Builder.CampaignContent.Label')">
              <b-input v-model="builder.campaignContent" :placeholder="$t('CampaignEdit.Assets.Builder.CampaignContent.Help')"></b-input>
            </b-field>
            <div class="share">
              <p class="share-title" v-t="'CampaignEdit.Assets.Builder.Share.Title'"></p>
              <p v-t="'CampaignEdit.Assets.Builder.Share.Help'"></p>
              <b-field :label="$t('CampaignEdit.Assets.Builder.Share.GeneratedURL')">
                <b-input type="textarea" :value="generatedURL" readonly></b-input>
              </b-field>
              <p class="share-title" v-t="'CampaignEdit.Assets.Builder.Table.Title'"></p>
              <p v-t="'CampaignEdit.Assets.Builder.Table.Help'"></p>
            </div>
          </template>
          <div class="b-table">
            <table class="table">
              <thead>
                <tr>
                  <th v-t="'CampaignEdit.Assets.Builder.Table.Columns.Parameter'"></th>
                  <th v-t="'CampaignEdit.Assets.Builder.Table.Columns.Required'"></th>
                  <th v-t="'CampaignEdit.Assets.Builder.Table.Columns.Example'"></th>
                  <th v-t="'CampaignEdit.Assets.Builder.Table.Columns.Description'"></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(n, i) in 6" :key="i">
                  <td>
                    <p v-t="`CampaignEdit.Assets.Builder.Table.Rows[${i}].Parameter[0]`"></p>
                    <code v-t="`CampaignEdit.Assets.Builder.Table.Rows[${i}].Parameter[1]`"></code>
                  </td>
                  <td v-t="`CampaignEdit.Assets.Builder.Table.Rows[${i}].Required`"></td>
                  <td><code v-t="`CampaignEdit.Assets.Builder.Table.Rows[${i}].Example`"></code></td>
                  <td v-html="$t(`CampaignEdit.Assets.Builder.Table.Rows[${i}].Description`)"></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </fragment>
</template>

<script lang="ts">
import {Component, Vue, Watch} from "vue-property-decorator";
import VueCookies from "vue-cookies";
import Fragment from "vue-fragment";
import {Action, Getter} from "vuex-class";
import i18n from "@/i18n";
import {DataSourceResult} from "@/models/DataSourceResult";
import {GridColumn} from "@/models/GridColumn";
import {SelectListItem} from "@/models/MultiselectItem";
import {ClientAsset} from "@/models/assets/ClientAsset";
import {AssetExternal} from "@/models/assets/AssetExternal";
import {AssetType} from "@/models/assets/AssetType";
import {SitePage} from "@/models/assets/SitePage";
import {Campaign, CampaignEditViewModel} from "@/models/campaigns/Campaign";
import {CampaignAsset, CampaignEditAssetsData} from "@/models/campaigns/CampaignAsset";
import CampaignService from "@/services/CampaignService";
import HelperService from "@/services/HelperService";
import CampaignsApiService, {CampaignsPayload} from "@/services/api/CampaignsApiService";
import IconBase from "../IconBase.vue";
import IconCaretDown from "../icons/IconCaretDown.vue";
import IconQuestionCircle from "../icons/IconQuestionCircle.vue";

Vue.use(Fragment.Plugin);
Vue.use(VueCookies);
const namespace = "campaignsModule";

@Component({
  components: {
    IconBase,
    IconCaretDown,
    IconQuestionCircle,
  },
})
export default class TabAssets extends Vue {

  //#region Vuex getters and actions

  @Getter("campaignEditViewModel", {namespace}) campaignEditViewModel!: CampaignEditViewModel;
  @Action("setCampaignEditViewModel", {namespace}) setCampaignEditViewModel!: (campaignEditViewModel: CampaignEditViewModel) => Promise<void>;

  //#endregion Vuex getters and actions


  //#region Properties

  campaignAssets?: DataSourceResult = {};
  campaignEditAssetsData?: CampaignEditAssetsData = {
    IsAddAssetEnabled: false,
    ProjectTypes: []
  };
  clientAssetsSearch = "";
  clientPropertiesSearch = "";
  page = 1;
  selectedAssetId = 0;
  selectedProjectType = AssetType.None;
  selectedPropertyId = 0;
  selectedSitePageId = 0;
  sitePagesSearch = "";
  clientAssets: ClientAsset[] = [];
  clientProperties: AssetExternal[] = [];
  sitePages: SitePage[] = [];

  builder = {
    websiteURL: "",
    campaignId: "",
    campaignSource: "",
    campaignMedium: "",
    campaignName: "",
    campaignTerm: "",
    campaignContent: "",
  };

  columns: GridColumn[] = [
    {
      id: 1,
      name: "ChannelId",
      label: "ID",
      visible: false,
      forbidden: false,
      searchable: true,
      sortable: true,
      width: 80
    },
    {
      id: 2,
      name: "ProjectId",
      label: i18n.t("CampaignEdit.Assets.Grid.Columns.ProjectId").toString(),
      class: "cell-project-id",
      searchable: false,
      sortable: false,
      visible: false,
      forbidden: false,
      width: 200
    },
    {
      id: 3,
      name: "Title",
      label: i18n.t("CampaignEdit.Assets.Grid.Columns.Title").toString(),
      class: "cell-project-title",
      searchable: false,
      sortable: false,
      visible: true,
      forbidden: false,
      width: 200
    },
    {
      id: 4,
      name: "ProjectTypeLocalized",
      label: i18n.t("CampaignEdit.Assets.Grid.Columns.ProjectTypeLocalized").toString(),
      class: "cell-project-type-localized",
      searchable: false,
      sortable: false,
      visible: true,
      forbidden: false,
      width: 100
    },
    {
      id: 5,
      name: "Url",
      label: i18n.t("CampaignEdit.Assets.Grid.Columns.Url").toString(),
      class: "cell-url",
      searchable: false,
      sortable: false,
      visible: true,
      forbidden: false
    },
    {
      id: 6,
      name: "IsDefault",
      label: i18n.t("CampaignEdit.Assets.Grid.Columns.IsDefault").toString(),
      class: "cell-is-default",
      searchable: false,
      sortable: false,
      visible: true,
      forbidden: false,
      width: 80
    },
  ];

  ProjectType = AssetType;

  //#endregion Properties


  //#region Watchers

  @Watch("campaignEditViewModel", {immediate: true})
  onCampaignEditViewModelChange(newCampaignEditViewModel: CampaignEditViewModel, oldCampaignEditViewModel: CampaignEditViewModel): void {
    if (!newCampaignEditViewModel) {
      return;
    }
    // this.campaignEditViewModel.Campaign.Id = 3954; // for test
    CampaignsApiService.getCampaignEditAssetsData(this.campaign.Id).then((data: CampaignEditAssetsData) => {
      this.campaignEditAssetsData = data;
    });
    this.refreshAssets();
  }

  //#endregion Watchers


  //#region Getters

  get campaign(): Campaign {
    return this.campaignEditViewModel?.Campaign;
  }

  get clientAssetsFiltered(): ClientAsset[] {
    return this.clientAssets.filter((i: ClientAsset) => i.title.toLowerCase().indexOf(this.clientAssetsSearch.toLowerCase()) >= 0);
  }

  get clientPropertiesFiltered(): AssetExternal[] {
    return this.clientProperties.filter((i: AssetExternal) => i.Title.toLowerCase().indexOf(this.clientPropertiesSearch.toLowerCase()) >= 0);
  }

  get selectedClientAsset(): ClientAsset | undefined {
    return this.clientAssets.find((i: ClientAsset) => i.id === +this.selectedAssetId);
  }

  get selectedClientProperty(): AssetExternal | undefined {
    return this.clientProperties.find((i: AssetExternal) => i.Id === +this.selectedPropertyId);
  }

  get selectedProjectTypeListItem(): SelectListItem | undefined {
    return this.campaignEditAssetsData?.ProjectTypes.find((i: SelectListItem) => +i.Value === +this.selectedProjectType);
  }

  get selectedSitePage(): SitePage | undefined {
    return this.sitePages.find((i: SitePage) => i.id === +this.selectedSitePageId);
  }

  get showAssetsSelectList(): boolean {
    return [AssetType.Custom, AssetType.Facebook, AssetType.Landing, AssetType.Site].indexOf(this.selectedProjectType) >= 0;
  }

  get showPropertiesSelectList(): boolean {
    return [AssetType.CustomLandingPage, AssetType.CustomSite, AssetType.ExternalAPI].indexOf(this.selectedProjectType) >= 0;
  }

  get showSitePagesList(): boolean {
    return this.selectedProjectType === AssetType.Site && this.selectedAssetId > 0;
  }

  get sitePagesFiltered(): SitePage[] {
    return this.sitePages.filter((i: SitePage) => i.title.toLowerCase().indexOf(this.sitePagesSearch.toLowerCase()) >= 0);
  }

  get generatedURL(): string {
    const url = new URL(this.builder.websiteURL);
    if (this.builder.campaignId) url.searchParams.set('utm_id', this.builder.campaignId);
    if (this.builder.campaignSource) url.searchParams.set('utm_source', this.builder.campaignSource);
    if (this.builder.campaignMedium) url.searchParams.set('utm_medium', this.builder.campaignMedium);
    if (this.builder.campaignName) url.searchParams.set('utm_campaign', this.builder.campaignName);
    if (this.builder.campaignTerm) url.searchParams.set('utm_term', this.builder.campaignTerm);
    if (this.builder.campaignContent) url.searchParams.set('utm_content', this.builder.campaignContent);
    this.setUtmParameters();
    return url.href;
  }

  //#endregion Getters


  //#region Handlers

  onDropdownActiveChange(show: boolean) {
    HelperService.onDropdownActiveChange(show);
  }

  onIsDefaultChange(asset: CampaignAsset) {
    CampaignsApiService.changeDefaultCampaignAsset(asset.ChannelId, asset.AssetId, asset.AssetTypeId, asset.AssetPageId).then(() => {
      this.refreshAssets();
    });
  }

  onSelectedAssetChange(assetId: number): void {
    this.campaign.AssetId = assetId;
    this.setCampaignEditViewModel(this.campaignEditViewModel);
    const loader = this.$loading.show();
    CampaignsApiService.getSitePages(this.campaign.CompanyId, assetId)
        .then((sitePages: SitePage[]) => {
          this.sitePages = sitePages;
          loader.hide();
          this.refreshAssets();
        });
  }

  onSelectedProjectTypeChange(assetType: AssetType): void {
    this.campaign.AssetType = assetType;
    this.campaign.AssetTypeId = +assetType;
    this.setCampaignEditViewModel(this.campaignEditViewModel);
    if ([AssetType.Custom, AssetType.Facebook, AssetType.Landing, AssetType.Site].indexOf(assetType) >= 0) {
      const loader = this.$loading.show();
      CampaignsApiService.getClientAssets(this.campaign.CompanyId, assetType, this.campaign.Id, this.campaign.AssetId)
          .then((clientAssets: ClientAsset[]) => {
            this.clientAssets = clientAssets;
            loader.hide();
          });
    } else if ([AssetType.CustomLandingPage, AssetType.CustomSite, AssetType.ExternalAPI].indexOf(assetType) >= 0) {
      const loader = this.$loading.show({
        container: this.$refs.assetsTableContainer
      });
      CampaignsApiService.getClientAutoCompleteProperties(this.campaign.CompanyId, assetType, this.campaign.Id, this.campaign.AssetId)
          .then((clientProperties: AssetExternal[]) => {
            this.clientProperties = clientProperties;
            loader.hide();
          });
    }
  }

  onSelectedPropertyChange(propertyId: number): void {
    this.campaign.AssetId = propertyId;
    this.setCampaignEditViewModel(this.campaignEditViewModel);
  }

  onSelectedSitePageChange(sitePageId: number): void {
    this.campaign.AssetPageId = sitePageId;
    this.setCampaignEditViewModel(this.campaignEditViewModel);
  }

  //#endregion Handlers


  //#region Private methods

  getColumnStyle(column: GridColumn): string {
    return HelperService.getColumnStyle(column);
  }

  refreshAssets() {
    const payload: CampaignsPayload = {
      channelId: this.campaign.Id,
      assetId: this.campaign.AssetId,
      page: this.page,
      pageSize: CampaignService.getPageSize(),
    };
    const loader = this.$loading.show({
      container: this.$refs.assetsTableContainer
    });
    CampaignsApiService.getCampaignAssets(payload).then((data: DataSourceResult) => {
      this.campaignAssets = data;
      this.fillBuilderData();
      loader.hide();
    });
  }
  
  fillBuilderData() {
    const campaignAsset = this.campaignAssets?.Data?.find(x => x.IsDefault);
    const utmParameters = JSON.parse(campaignAsset?.UtmParameters || "{}");
    this.builder.websiteURL = (campaignAsset?.PreparedUrlList || campaignAsset?.PreparedMobileUrlList || []).slice(0, 1) ?? "";
    this.builder.campaignId = utmParameters['utm_id'] ?? "";
    this.builder.campaignSource = utmParameters['utm_source'] ?? "";
    this.builder.campaignMedium = utmParameters['utm_medium'] ?? "";
    this.builder.campaignName = utmParameters['utm_campaign'] ?? "";
    this.builder.campaignTerm = utmParameters['utm_term'] ?? "";
    this.builder.campaignContent = utmParameters['utm_content'] ?? "";
    this.campaign.UtmParameters = utmParameters;
  }

  setUtmParameters() {
    this.campaign.UtmParameters = {
      utm_id: this.builder.campaignId,
      utm_source: this.builder.campaignSource,
      utm_medium: this.builder.campaignMedium,
      utm_campaign: this.builder.campaignName,
      utm_term: this.builder.campaignTerm,
      utm_content: this.builder.campaignContent,
    };
  }

  //#endregion Private methods
}
</script>

<style lang="scss">
@import "@/assets/styles/variables";

h5.section-title {
  font-weight: bold;
  margin-bottom: 1rem;
}

.assets-tooltip {
  margin: 0 .3rem;
}

.connected-assets-desc {
  margin-bottom: 1rem;
}

.connected-assets-title {
  font-weight: bold;
}

.dropdown-w {
  margin-bottom: 1rem;
}

.table {
  .cell-is-default {
    text-align: center;
  }
}

.assets-builder {
  min-height: 300px;

  .title {
    margin: 0 -1.5rem;
    padding: 12px 30px 13px;
    line-height: 22px;
    border-top: 1px solid #d3d6e2;
    border-bottom: 1px solid #d3d6e2;
    display: flex;
    align-items: center;
    font-weight: 600;
    font-size: 18px;
  }

  .dropdown-w {
    .dropdown,
    .button {
      width: 100%;
    }
  }

  .share {
    margin-bottom: 1rem;

    .share-title {
      font-size: 1.5rem;
    }
  }
}

</style>
